/* eslint-disable max-len */
import { RightCircleFilled } from '@ant-design/icons';
import { Collapse, Form, Row, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { Store } from 'antd/lib/form/interface';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputPassword from 'components/atoms/InputPassword';
import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { leiMigraceApi } from 'services/lei/leiLogin';
import loginApi from 'services/login';
import { loginBankID } from 'services/loginBankID';
import { loginCertApiNew } from 'services/loginCert';
import { ETypPrihlaseni } from 'services/profily';

import routePathEnum from 'enums/routePathEnum';

import { validateLEI } from 'utils/validations/leiValidation';

interface ILogin {
    loginOptions: ETypPrihlaseni[];
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    akcionar?: number;
    akcionarLogin?: string;
}

interface IErrorMsgs {
    jmenoHeslo: string;
    pxKlic: string;
    cert: string;
    bankId: string;
    lei: string;
    failedAttemptsErr: string;
}

const LoginCollapse: React.FC<ILogin> = ({ loginOptions, setLoading, akcionar, akcionarLogin }) => {
    const navigate = useNavigate();
    // const { leiRedirect } = useLeiRedirect();
    const { language, leiLogin, lei } = useContext(Context);
    const { tokenURL } = useParams<{ tokenURL: string }>();
    const { setLevel } = useProfily();
    const [form] = useForm();
    const [privateCert, setPrivateCert] = useState<string>('');
    const [privateCertIssuer, setPrivateCertIssuer] = useState<string>('');
    const [pxklicWaiting, setPxklicWaiting] = useState<boolean>(false);
    const [typPrihlaseni, setTypPrihlaseni] = useState<ETypPrihlaseni | undefined>(
        loginOptions[0] === ETypPrihlaseni.PXKLIC ? ETypPrihlaseni.UZIV : loginOptions[0]
    );
    const [modalOpen] = useState<string>('');
    const errMsgsInitialStates = {
        jmenoHeslo: '',
        pxKlic: '',
        cert: '',
        bankId: '',
        lei: '',
        failedAttemptsErr: ''
    };
    const [errMessages, setErrMessages] = useState<IErrorMsgs>(errMsgsInitialStates);
    const resetErrMessages = () => {
        setErrMessages(errMsgsInitialStates);
    };
    const { Panel } = Collapse;

    const loginErrFunc = (err: any, values: any) => {
        if (err.request.status === 401) {
            err.response?.data.message === 'PX klic neni potvrzen.' ||
            err.response?.data.message === 'PX klic neni nastaven.'
                ? navigate(routePathEnum.PXKLIC_LOGIN)
                : err.response?.data.message === 'Pristup uzivatele docasne blokovan.'
                  ? setErrMessages(pv => {
                        return {
                            ...pv,
                            failedAttemptsErr: 'FAILED_ATTEMPTS_ERR'
                        };
                    })
                  : setErrMessages(pv => {
                        return {
                            ...pv,
                            jmenoHeslo: window.location.href.includes(routePathEnum.LEI_LOGIN_MUJDEP)
                                ? validateLEI(values.username) !== 0
                                    ? 'LOGIN_LEI_INVALID'
                                    : 'LEI_PRIHLASENI_LEI_MENO'
                                : 'LOGIN_INVALID'
                        };
                    });
        }
        if (err.request.status === 409)
            setErrMessages(pv => {
                return {
                    ...pv,
                    jmenoHeslo:
                        err.response?.data.message === 'Uzivatel byl deaktivovan.'
                            ? 'LOGIN_ERR_409'
                            : 'LOGIN_ERR_409_ALT'
                };
            });
    };

    // Pokud je formular validni volame metodu loginApi a ta nastavuje token do store
    const onFinish = (values: Store) => {
        setLoading(true);
        // Nutno rozlišit volání z localhostu (nefungují služby volané z webratio (Private) kvůli CORS), s tím nehneme bohužel
        // pokud bychom neobešli použitím "druhého" loginu, nemůžeme vyvíjet z localhostu

        loginApi(values.username.toLowerCase(), values.password, language)
            .then(resp => {
                if (resp.pxklic && !pxklicWaiting) {
                    setPxklicWaiting(true);
                    setLoading(true);
                    loginApi(values.username.toLowerCase(), values.password, language, true)
                        .then(response => {
                            window.location.hash.includes(routePathEnum.LOGIN_JINA_OSOBA) &&
                                setLevel('prevPath', window.location.hash);
                            setLevel('token', response.token);
                            akcionar === 200
                                ? navigate(`${routePathEnum.AKCIONAR_SMS}/${tokenURL}`)
                                : navigate(routePathEnum.MAIN_PAGE);
                        })
                        .catch(err => loginErrFunc(err, values))
                        .finally(() => {
                            setPxklicWaiting(false);
                            setLoading(false);
                        });
                } else if (resp.token) {
                    if (lei?.loginless) {
                        setLevel('token', resp.token);
                    } else if (leiLogin) {
                        leiMigraceApi(leiLogin?.leimigraceid || '', resp.token).then(() => {
                            setLevel('token', resp.token);
                            setLevel('leiLogin', null);
                            navigate(routePathEnum.MAIN_PAGE);
                        });
                    } else {
                        window.location.hash.includes(routePathEnum.LOGIN_JINA_OSOBA) &&
                            setLevel('prevPath', window.location.hash);
                        setLevel('token', resp.token);
                        akcionar === 200
                            ? navigate(`${routePathEnum.AKCIONAR_SMS}/${tokenURL}`)
                            : navigate(routePathEnum.MAIN_PAGE);
                    }
                } else {
                    setLoading(false);
                    setErrMessages(pv => {
                        return {
                            ...pv,
                            jmenoHeslo: 'LOGIN_INVALID'
                        };
                    });
                }
            })
            .catch(err => {
                loginErrFunc(err, values);
                setLoading(false);
            });
    };

    const onFinishCert = () => {
        setLoading(true);
        loginCertApiNew()
            .then(resp => {
                if (lei?.loginless) {
                    setLevel('token', resp.data.token);
                } else if (leiLogin) {
                    leiMigraceApi(leiLogin?.leimigraceid || '', resp.data.token).then(() => {
                        setLevel('token', resp.data.token);
                        navigate(routePathEnum.MAIN_PAGE);
                    });
                } else {
                    window.location.hash.includes(routePathEnum.LOGIN_JINA_OSOBA) &&
                        setLevel('prevPath', window.location.hash);
                    setLevel('token', resp.data.token);
                    navigate(routePathEnum.MAIN_PAGE);
                }
            })
            .catch(err => {
                if (err.request.status === 401) {
                    setErrMessages(pv => {
                        return {
                            ...pv,
                            cert: 'EMP_VYBER_PROFIL_RESP_401_NENALEZEN'
                        };
                    });
                    setPrivateCert(err.response.data.dn);
                    setPrivateCertIssuer(err.response.data.issuer);
                } else {
                    setErrMessages(pv => {
                        return {
                            ...pv,
                            cert: 'EMP_VYBER_PROFIL_RESP_401_CHYBI'
                        };
                    });
                }
            })
            .finally(() => {
                setLoading(false);
                !errMessages.cert &&
                    setErrMessages(pv => {
                        return {
                            ...pv,
                            cert: 'EMP_VYBER_PROFIL_RESP_401_CHYBI'
                        };
                    });
            });
    };
    const onFinishBankID = () => {
        setLoading(true);
        loginBankID(language)
            .then(resp => window.location.replace(resp.redirect))
            .finally(() => setLoading(false));
    };

    return (
        <>
            <Space
                size="middle"
                direction="vertical"
                style={{
                    width: '100%',
                    marginTop: 16,
                    marginBottom: 30
                }}
            >
                {(loginOptions.includes(ETypPrihlaseni.UZIV) || loginOptions.includes(ETypPrihlaseni.PXKLIC)) && (
                    <Collapse
                        expandIconPosition="right"
                        activeKey={typPrihlaseni ? [typPrihlaseni] : []}
                        onChange={() => {
                            setTypPrihlaseni(pv => (pv === ETypPrihlaseni.UZIV ? undefined : ETypPrihlaseni.UZIV));
                            resetErrMessages();
                        }}
                    >
                        <Panel header={<TranslateText>LOGIN_JMENOHESLO</TranslateText>} key={ETypPrihlaseni.UZIV}>
                            <div
                                style={{
                                    margin: 10
                                }}
                            >
                                <FormWrapper
                                    onFinish={onFinish}
                                    outForm={form}
                                    onFinishFailed={err => console.log(err)}
                                >
                                    <InputComponent
                                        disabled={!!(akcionarLogin || leiLogin?.mail)}
                                        initialValue={akcionarLogin || leiLogin?.mail}
                                        label="UZIVATELSKE_JMENO"
                                        name="username"
                                        placeholder="E-mail"
                                    />
                                    <InputPassword label="HESLO" name="password" type={'password'} />
                                    <AnchorComponent
                                        link={true}
                                        to={routePathEnum.ZADOST_OBNOVENI_HESLA}
                                        style={{
                                            float: 'right'
                                        }}
                                        onClick={() => setLevel('prevPath', window.location.hash)}
                                    >
                                        <TranslateText>ZAPOMENUTE_HESLO</TranslateText>
                                    </AnchorComponent>
                                    <ButtonComponent
                                        htmlType="button"
                                        icon={<RightCircleFilled />}
                                        id="jmenoHesloSubmitBtn"
                                        buttontext="PRIHLASIT_SE"
                                        extramessage={errMessages.failedAttemptsErr || errMessages.jmenoHeslo}
                                        onClick={() => form.submit()}
                                    />
                                </FormWrapper>
                            </div>
                        </Panel>
                    </Collapse>
                )}
                {loginOptions.includes(ETypPrihlaseni.CERT) && (
                    <Collapse
                        expandIconPosition="right"
                        activeKey={typPrihlaseni ? [typPrihlaseni] : []}
                        onChange={() => {
                            setTypPrihlaseni(pv => (pv === ETypPrihlaseni.CERT ? undefined : ETypPrihlaseni.CERT));
                            resetErrMessages();
                        }}
                    >
                        <Panel header={<TranslateText>LOGIN_CERT</TranslateText>} key={ETypPrihlaseni.CERT}>
                            <div
                                style={{
                                    margin: 10
                                }}
                            >
                                <FormWrapper onFinish={() => {}}>
                                    <Form.Item
                                        label={
                                            <span className="komercni-certifikat-nadpis">
                                                <TranslateText>KOMERCNI_CERTIFIKAT</TranslateText>
                                            </span>
                                        }
                                    >
                                        {privateCert && (
                                            <>
                                                <Paragraph
                                                    text={'CERTIFIKAT'}
                                                    style={{
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                : <Paragraph text={privateCert} />
                                            </>
                                        )}
                                        {privateCertIssuer && (
                                            <div
                                                style={{
                                                    display: 'block',
                                                    paddingBottom: 8
                                                }}
                                            >
                                                <Paragraph
                                                    text={'EMP_LOGIN_CERTIFIKAT_VYSTAVITEL'}
                                                    style={{
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                : <Paragraph text={privateCertIssuer} />
                                            </div>
                                        )}
                                    </Form.Item>
                                    <ButtonComponent
                                        id="certLoginSubmitBtn"
                                        buttontext="VSTOUPIT"
                                        onClick={onFinishCert}
                                        icon={<RightCircleFilled />}
                                        extramessage={errMessages.cert}
                                    />
                                </FormWrapper>
                            </div>
                        </Panel>
                    </Collapse>
                )}
                {loginOptions.includes(ETypPrihlaseni.BI) && (
                    <Collapse
                        expandIconPosition="right"
                        activeKey={typPrihlaseni ? [typPrihlaseni] : []}
                        onChange={() => {
                            setTypPrihlaseni(pv => (pv === ETypPrihlaseni.BI ? undefined : ETypPrihlaseni.BI));
                            resetErrMessages();
                        }}
                    >
                        <Panel header={<TranslateText>LOGIN_BANKID</TranslateText>} key={ETypPrihlaseni.BI}>
                            <div
                                style={{
                                    margin: 10
                                }}
                            >
                                <FormWrapper onFinish={() => {}}>
                                    <ButtonComponent
                                        id="certLoginSubmitBtn"
                                        buttontext="VSTOUPIT"
                                        onClick={onFinishBankID}
                                        icon={<RightCircleFilled />}
                                        extramessage={errMessages.cert}
                                    />
                                </FormWrapper>
                            </div>
                        </Panel>
                    </Collapse>
                )}
            </Space>

            <Modal
                open={!!modalOpen}
                onCancel={() => {
                    setLevel('token', modalOpen);
                    navigate(routePathEnum.MAIN_PAGE);
                }}
                title={<TranslateText>LEI_LOGIN_SUCCESS</TranslateText>}
                footer={[
                    <Row justify="end">
                        <ButtonComponent
                            buttontext="LEI_LOGIN_SUCCESS_BUTTON"
                            type="primary"
                            id="leiSuccessRedirectBtn"
                            onClick={() => {
                                setLevel('token', modalOpen);
                                navigate(routePathEnum.MAIN_PAGE);
                            }}
                        />
                    </Row>
                ]}
            >
                <Paragraph text="LEI_LOGIN_SUCCESS_TEXT" />
            </Modal>

            <Modal closable={false} maskClosable={false} keyboard={false} footer={false} open={pxklicWaiting}>
                <Row
                    style={{
                        paddingTop: 10
                    }}
                    justify="center"
                >
                    <Paragraph text="PXKLIC_LOGIN_POPUPL" />
                </Row>
            </Modal>
        </>
    );
};

export default LoginCollapse;
